<template>
  <TeaserPoster
    v-bind="mutatedProps"
    :fluid="true"
    class="banner"
    heading-size="heading-md"
    :class="variantClass"
    :show-date="showDate"
    date-position="top"
    :renditions="computedRenditions"
    :analytics="analytics"
  />
</template>

<script setup lang="ts">
// this simply wraps a TeaserPoster, mutates the props and gives a min-height, to account for the Banner specific settings.

import type { ICmsBanners } from '~~/types/_cms-types';
import type { IAnalytics, IImageLoadingSettings } from '~~/types/local_types';

interface IProps extends ICmsBanners, IImageLoadingSettings {
  showDate?: boolean;
  isPresenter?: boolean;
  analytics?: IAnalytics;
}

const props = withDefaults(defineProps<IProps>(), {
  loading: 'lazy',
  priority: 'auto',
  showDate: false,
  isPresenter: false,
  analytics: undefined,
});

const computedRenditions = computed(() => {
  return [
    {
      breakpoint: 'sm',
      size: 640,
      aspectRatio: props.isPresenter ? '16x9' : '4x3',
    },
  ];
});

const bannerTeaserAdapter = (input: IProps) => {
  const data = {
    collection: 'banner',
    teaserImage: input.image,
    label: input.subTitle,
    ...input,
  };

  delete data.image;
  delete data.subTitle;

  return data;
};

const mutatedProps = computed((): object => {
  return bannerTeaserAdapter(props);
});

const variantClass = computed(() => {
  return props.isPresenter ? 'is--presenter' : '';
});
</script>

<style scoped lang="postcss">
.banner {
  --min-height: 153px;
  min-height: var(--min-height);

  background: linear-gradient(
    0deg,
    hsl(var(--color-primary-950)) 0%,
    hsl(var(--color-primary-850)) 100%
  );

  &::before {
    content: unset !important;
  }

  @media (--sm-viewport) {
    aspect-ratio: 339/153;
  }

  @media (--md-viewport) {
    &::before {
      content: '' !important;
      display: block;
      padding-top: 129% !important;
    }
    --min-height: unset;
  }

  &:deep(.poster-image) {
    display: block;
    height: 100%;
    position: relative;

    .image {
      --height: 120%;
      --top: -2.5%;
      --right: 25%;
      --transform: translateX(50%);
      --mask: linear-gradient(
        90deg,
        hsla(var(--color-neutral-black), 0) 0,
        hsla(var(--color-neutral-black), 0.5) 25%,
        hsl(var(--color-neutral-black)) 67.8%
      );
      width: auto;
      height: var(--height);
      top: var(--top);
      right: var(--right);
      transform: var(--transform);
      mask-image: var(--mask);

      @media (--md-viewport) {
        width: 100%;
        --height: calc(100% - var(--unit-40));
        --top: -2.5%;
        --right: unset;
        --transform: unset;
        --mask: linear-gradient(
          180deg,
          hsl(var(--color-neutral-black)) 35%,
          hsla(var(--color-neutral-black), 0.2) 75%,
          hsla(var(--color-neutral-black), 0) 100%
        );
      }
    }

    .meta {
      background: unset;
      --max-width: 60%;
      max-width: var(--max-width);

      @media (--sm-viewport) {
        padding-right: 0;
        hyphens: none;
      }

      @media (--md-viewport) {
        --max-width: unset;
      }
    }
  }

  &.is--presenter {
    &:deep(.poster-image) {
      .image {
        --height: 170%;
        --right: 30%;
        --top: 0%;
        @media (--md-viewport) {
          --right: unset;
          --height: 100%;
        }
      }
    }
  }
}
</style>
